import React from "react";
import { DISCOVERY } from "@/constants/testIds";

// Ida's high-level Creative Recommendation. Inspires without giving the plan away.
// Falls back to the legacy schema (budget/timeline) for projects created before
// the approval workflow existed.
export function RecommendationView({ rec, user }) {
  if (!rec) return null;
  const firstName = user?.name ? user.name.split(" ")[0] : "";
  const title = rec.project_title || "Your project";
  const legacy = !!rec.estimated_budget;

  return (
    <div
      className="animate-[fadeUp_400ms_ease-out_both] max-w-[880px] mx-auto text-center"
      data-testid={DISCOVERY.recommendation}
    >
      <div className="eyebrow eyebrow-denim">Your Producer</div>
      <p className="mt-5 font-display text-ink text-[22px] md:text-[28px] leading-[1.35] tracking-[-0.012em] max-w-[720px] mx-auto font-light">
        {rec.opening_thought || `${firstName ? `Thanks, ${firstName}.` : "Thanks."} Here${"\u2019"}s where we think this lands.`}
      </p>

      <h2 className="mt-10 md:mt-14 font-display font-extrabold text-ink leading-[1.02] tracking-[-0.035em] text-[30px] sm:text-[40px] md:text-[52px]">
        {title}
      </h2>

      {rec.creative_direction && (
        <div className="mt-14 md:mt-16 max-w-[640px] mx-auto" data-testid="rec-creative-direction">
          <div className="eyebrow eyebrow-denim">Creative Direction</div>
          <p className="text-ink text-[16.5px] md:text-[18px] mt-5 leading-[1.8]">
            {rec.creative_direction}
          </p>
        </div>
      )}

      {Array.isArray(rec.suggested_deliverables) && rec.suggested_deliverables.length > 0 && (
        <div className="mt-14 md:mt-16 max-w-[560px] mx-auto" data-testid="rec-deliverables">
          <div className="eyebrow eyebrow-denim">Suggested Deliverables</div>
          <div className="mt-6 divide-y divide-[#eaeaea] border-t border-b border-[#eaeaea] text-left">
            {rec.suggested_deliverables.map((d, i) => (
              <div key={i} className="py-4 text-ink text-[16px] md:text-[17px]">{d}</div>
            ))}
          </div>
        </div>
      )}

      {rec.production_approach?.approach && (
        <div className="mt-14 md:mt-16" data-testid="rec-approach">
          <div className="eyebrow eyebrow-denim">Recommended Approach</div>
          <div className="mt-4 font-display font-extrabold text-ink text-[26px] md:text-[34px] leading-[1.1] tracking-[-0.02em]">
            {rec.production_approach.approach}
          </div>
          {rec.production_approach.why && (
            <p className="text-muted-ink text-[16px] md:text-[17px] mt-5 leading-[1.75] max-w-[560px] mx-auto">
              {rec.production_approach.why}
            </p>
          )}
        </div>
      )}

      {Array.isArray(rec.worth_exploring) && rec.worth_exploring.length > 0 && (
        <div className="mt-14 md:mt-16 max-w-[600px] mx-auto" data-testid="rec-worth-exploring">
          <div className="eyebrow eyebrow-denim">Worth Exploring Together</div>
          <div className="mt-6 space-y-4 text-left">
            {rec.worth_exploring.map((q, i) => (
              <p key={i} className="text-ink text-[16px] md:text-[17px] leading-[1.7] font-serif-italic border-l-2 border-denim-bnm pl-5">
                {q}
              </p>
            ))}
          </div>
        </div>
      )}

      {rec.inspiration && (
        <div className="mt-14 md:mt-16 max-w-[600px] mx-auto" data-testid="rec-inspiration">
          <p className="text-muted-ink text-[16px] md:text-[17.5px] leading-[1.8] italic">
            {rec.inspiration}
          </p>
        </div>
      )}

      {legacy && (
        <>
          <div className="mt-14 md:mt-16" data-testid={DISCOVERY.recBudget}>
            <div className="eyebrow eyebrow-denim">Estimated Budget</div>
            <div className="mt-4 font-display font-extrabold text-ink text-[32px] md:text-[44px] leading-[1.1] tracking-[-0.02em]">
              {rec.estimated_budget.range}
            </div>
            {rec.estimated_budget.note && (
              <p className="text-muted-ink text-[15px] md:text-[16.5px] mt-5 leading-[1.75] max-w-[560px] mx-auto">
                {rec.estimated_budget.note}
              </p>
            )}
          </div>
          {Array.isArray(rec.estimated_timeline?.phases) && (
            <div className="mt-14 max-w-[620px] mx-auto" data-testid={DISCOVERY.recTimeline}>
              <div className="eyebrow eyebrow-denim">Estimated Timeline</div>
              <div className="mt-7 divide-y divide-[#eaeaea] border-t border-b border-[#eaeaea] text-left">
                {rec.estimated_timeline.phases.map((p, i) => (
                  <div key={i} className="py-4 flex items-center justify-between gap-6">
                    <div className="text-ink text-[16px]">{p?.name}</div>
                    <div className="text-muted-ink text-[15px]">{p?.duration}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {rec.best_way_forward?.approach && (
            <div className="mt-14" data-testid={DISCOVERY.recBestWay}>
              <div className="eyebrow eyebrow-denim">Best Way Forward</div>
              <div className="mt-4 font-display font-extrabold text-ink text-[26px] md:text-[34px] leading-[1.1] tracking-[-0.02em]">
                {rec.best_way_forward.approach}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RecommendationView;
